import React from "react";

// Request bodies are checked against the published OpenAPI contract before release.
export const CONTINUITY_EXAMPLES = {
  worker: {
    name: "Vendor Operations",
    availability: "24_7",
    instructions: "Screen vendors using the approved checklist. Return sources and unresolved issues.",
    skills: [{ name: "Evidence checklist", instructions: "Capture the source URL and screenshot for each finding. Flag uncertain matches for review." }],
    memory: { enabled: true },
  },
  feedback: { message: "Prioritize the vendors with missing registration details.", interrupt: false },
  answer: { answer: "Use the registered legal entity name from the attached file." },
  continuation: { work: "Review the unresolved vendors and return an updated report.", guardrails: { timeout: 600000 } },
  schedule: {
    kind: "schedule",
    assignment: { work: "Review the new vendor queue and return an evidence report.", guardrails: { timeout: 600000 } },
    schedule: { everySeconds: 3600, maxRuns: 24 },
  },
  access: { credentialDefaults: { vaultCredentials: ["YOUR_VAULT_LOGIN_ID"], credentialConnections: [], agentIdentity: "" } },
  tool: {
    name: "lookup_vendor",
    execution: "client",
    description: "Look up a vendor in our application.",
    inputSchema: { type: "object", required: ["vendorId"], properties: { vendorId: { type: "string" } }, additionalProperties: false },
  },
};

function RequestExample({ method = "POST", path, body }) {
  return (
    <div className="docs-code-block">
      <div className="docs-code-header"><span className="docs-lang" style={{ textTransform: "none", overflowWrap: "anywhere" }}>{method} {path}</span></div>
      <pre className="docs-code-content" style={{ margin: 0 }}>{JSON.stringify(body, null, 2)}</pre>
    </div>
  );
}

export default function WorkerContinuityGuide() {
  return (
    <>
      <section id="worker-continuity" className="docs-section">
        <h2 className="docs-section-title">Persistent Workers &amp; Follow-up</h2>
        <p className="docs-text">
          Create a worker once, then hand it successive assignments. Its identity, saved
          instructions, and named skills remain available between runs. Enable memory to retain
          compact context across assignments. These settings save guidance; they do not train model weights.
        </p>
        <RequestExample path="/v1/workers" body={CONTINUITY_EXAMPLES.worker} />
        <p className="docs-text">
          Use the returned worker ID with <code>POST /v1/workers/{"{workerId}"}/assign</code>.
          Update future work with <code>PATCH /v1/workers/{"{workerId}"}</code>; updates are rejected
          while an assignment is active. An assignment&apos;s <code>instructions</code> override saved
          instructions and its <code>skills</code> replace saved skills for that assignment only.
          An empty string or array clears the corresponding guidance for that assignment.
        </p>
        <h3 className="docs-subtitle">Guide work already in progress</h3>
        <RequestExample path="/v1/runs/{runId}/feedback" body={CONTINUITY_EXAMPLES.feedback} />
        <p className="docs-text">
          Feedback is consumed at the next decision boundary. Set <code>interrupt: true</code>
          to yield there and checkpoint progress for follow-up. Feedback adds guidance, not new
          credentials, tools, or authority. It is not a separate concurrent chat service.
          Team-root feedback reaches the lead and helpers; child-run feedback reaches that child.
        </p>
        <p className="docs-text">
          When the worker needs a missing fact, read <code>GET /v1/runs/{"{runId}"}/questions</code>,
          then answer the question ID or send <code>{'{"decline": true}'}</code>.
          Identical retries return the accepted answer; a different second answer conflicts.
          A late answer cannot restart an expired or terminal run. Use separate approval and
          authentication interfaces for those waits; never send passwords or MFA codes in an answer.
        </p>
        <RequestExample path="/v1/runs/{runId}/questions/{questionId}/answer" body={CONTINUITY_EXAMPLES.answer} />
        <h3 className="docs-subtitle">Continue a finished assignment</h3>
        <p className="docs-text">
          Set <code>retainContext: true</code> when submitting the source run to preserve compact
          facts and action history. After it becomes terminal, inspect <code>/checkpoints</code>
          or start follow-up with <code>/continue</code>. Older runs without retained context may
          have only their task, files, and result available.
        </p>
        <RequestExample path="/v1/runs/{runId}/continue" body={CONTINUITY_EXAMPLES.continuation} />
        <p className="docs-text">
          Continuation creates a new billed execution with a fresh browser and its own deadline.
          Source credentials and pending actions are not copied. Input-file references are inherited
          only when resources are omitted; explicit resources replace that list. Supply the access
          the new work requires. Set <code>workerId</code> to use a persistent worker&apos;s current
          configuration, and continue a team parent rather than an individual child.
        </p>
        <p className="docs-text">
          For a correction, <code>POST /v1/runs/{"{runId}"}/remediations</code> accepts follow-up
          work plus either <code>actionSequence</code> or <code>toolCallId</code>. It creates linked
          correction work; reversal depends on the external system and is not a guaranteed undo.
          To edit and resubmit a terminal root run, retrieve its <code>/retry-draft</code> when the
          selected files and credentials are still usable.
        </p>
      </section>

      <section id="worker-automations" className="docs-section">
        <h2 className="docs-section-title">Schedules, Events &amp; Queued Work</h2>
        <p className="docs-text">
          A persistent identity does not reserve a desktop while idle. Availability controls
          when assignments may execute; use automations to generate recurring work and the turns
          inbox to queue it. Pending triggers and queued work do not consume execution time.
          Execution and waits inside an active run are billed.
        </p>
        <RequestExample path="/v1/workers/{workerId}/automations" body={CONTINUITY_EXAMPLES.schedule} />
        <ul className="docs-list">
          <li><strong>Schedules:</strong> fixed UTC intervals from 60 seconds to one year. Missed ticks coalesce; at most one occurrence remains unfinished. <code>maxRuns: 0</code> means until canceled.</li>
          <li><strong>Event triggers:</strong> use <code>kind: &quot;trigger&quot;</code> with an assignment and <code>trigger.eventType</code>, plus optional top-level <code>match</code> fields. Call <code>/automations/{"{automationId}"}/fire</code> with <code>eventId</code>, <code>type</code>, and <code>data</code>. Verify third-party webhooks in your application first.</li>
          <li><strong>Event deduplication:</strong> reuse the same <code>eventId</code> for an identical retry. A changed payload conflicts. Event data does not confer additional authority.</li>
          <li><strong>Workflows:</strong> use <code>kind: &quot;workflow&quot;</code> with up to 32 dependency steps. Steps run after their dependencies succeed; inspect the automation&apos;s <code>/turns</code> for progress and errors.</li>
        </ul>
        <p className="docs-text">
          <code>POST /v1/workers/{"{workerId}"}/turns</code> accepts an assignment while the worker
          is busy or paused. A future <code>scheduleAt</code> waits without reserving the worker,
          unlike scheduled <code>/assign</code>. Admission is checked again at dispatch; each turn
          can bind to only one run. Reuse its idempotency key for retries.
        </p>
        <p className="docs-text">
          Pause an automation to stop future emission; accepted turns may still proceed. Cancel
          requests cancellation of unfinished turns and admitted runs without reversing prior effects.
          Terminal automations cannot resume. Persistent availability supports <code>24_7</code>,
          <code>full_time</code>, <code>part_time</code>, or <code>custom</code> with an explicit schedule.
          Default full-time and part-time hours are Monday–Friday 09:00–17:00 and 09:00–13:00 UTC;
          custom schedules require <code>availabilitySchedule</code>.
        </p>
      </section>

      <section id="saved-access" className="docs-section">
        <h2 className="docs-section-title">Vault &amp; Saved Access</h2>
        <p className="docs-text">
          GrupaAI Vault stores managed browser logins. Use <code>GET /v1/vault</code> with the
          same API key that will submit work, then pass usable IDs in <code>resources.vaultCredentials</code>.
          Personal logins belong to their creator; API key access must be granted explicitly.
          Keys can use authorized logins without reading stored passwords. Restrict logins to exact
          HTTPS <code>allowedOrigins</code> and review their <code>/activity</code> audit events.
        </p>
        <p className="docs-text">
          Save reusable access on an idle persistent worker with <code>credentialDefaults</code>.
          The three fields below are required when setting defaults; replace the example ID with one
          returned by Vault. Every assignment rechecks authorization, and unusable defaults are withheld.
        </p>
        <RequestExample method="PATCH" path="/v1/workers/{workerId}" body={CONTINUITY_EXAMPLES.access} />
        <p className="docs-text">
          Zero-custody broker connections remain available through <code>credentialConnections</code>
          and <code>agentIdentity</code>. In Playground, authorized selections are restored on retry
          and worker selection; <strong>Save access defaults</strong> persists them for future work.
          Saved selections contain opaque references, never returned passwords.
        </p>
      </section>

      <section id="client-tools" className="docs-section">
        <h2 className="docs-section-title">Application Tools &amp; Deliverables</h2>
        <p className="docs-text">
          Add a custom tool to <code>resources.customTools</code>. Use <code>execution: &quot;client&quot;</code>
          when your application will execute it; provide an <code>inputSchema</code> for validated
          arguments. HTTP tools instead use an endpoint. Client tools cannot include an endpoint or auth value.
        </p>
        <RequestExample method="CUSTOM TOOL" path="resources.customTools[]" body={CONTINUITY_EXAMPLES.tool} />
        <ol className="docs-list">
          <li>Read <code>GET /v1/runs/{"{runId}"}/tool-calls</code> for requests.</li>
          <li>Persist a unique <code>claimId</code> (16–200 characters), then POST it to the call&apos;s <code>/claim</code>. Execute only after obtaining ownership.</li>
          <li>Persist your result before POSTing <code>claimId</code>, <code>output</code> (a string up to 128 KiB), and optional <code>isError</code> to <code>/output</code>.</li>
        </ol>
        <p className="docs-text">
          Identical claims and outputs can be retried; competing claims or changed outputs conflict.
          Claims are not automatically reassigned because an external effect may have occurred.
          Persist ownership and results so a delivery retry does not repeat your application&apos;s side effect.
          Active-run deadlines and runtime charges continue during a tool wait.
        </p>
        <p className="docs-text">
          Download completed app exports and workspace deliverables from the run&apos;s
          <code>/artifacts</code> endpoints or Playground. A separate Drive upload is unnecessary.
          For a root run with a structured output contract, <code>GET /v1/runs/{"{runID}"}/output-review</code>
          exposes accepted records and coverage. While the run is active, POST a known bad record&apos;s
          <code>deliverable_id</code>, <code>key</code>, <code>fence</code>, and <code>reason</code> to request
          reinspection; this withdraws acceptance without injecting replacement source data.
        </p>
      </section>
    </>
  );
}
